/**
 * Client-side .xlsx export (R1-08, R2-10) via write-excel-file — a write-only
 * library (no spreadsheet parsing, so none of the `xlsx` package's parser CVEs).
 * Loaded on demand so it never weighs on page load.
 */
export type XCell = string | number | null | undefined;

export interface XSheet {
  sheet: string;
  /** Optional title rows written above the table (bold first line). */
  title?: string[];
  header: string[];
  rows: XCell[][];
  /** Column indexes to format as dollars. */
  money?: number[];
  /** Column indexes to format as percentages (values are fractions). */
  percent?: number[];
  /** Per-cell number format (wins over column formats), e.g. when each ROW is a different metric. */
  cellFormat?: (rowIndex: number, colIndex: number) => string | undefined;
}

export async function downloadXlsx(fileName: string, sheets: XSheet[]): Promise<void> {
  const { default: writeExcelFile } = await import("write-excel-file/browser");
  const data = sheets.map((s) => {
    const titleRows = (s.title ?? []).map((t, i) => [{ value: t, fontWeight: i === 0 ? "bold" : undefined }]);
    const spacer = s.title?.length ? [[{ value: "" }]] : [];
    const header = s.header.map((h) => ({ value: h, fontWeight: "bold" }));
    const body = s.rows.map((r, ri) =>
      r.map((v, i) => {
        if (v === null || v === undefined || v === "") return null;
        if (typeof v === "number") {
          const format = s.cellFormat?.(ri, i)
            ?? (s.money?.includes(i) ? "$#,##0.00" : s.percent?.includes(i) ? "0.0%" : undefined);
          return { value: v, type: Number, format };
        }
        return { value: v, type: String };
      }),
    );
    return {
      data: [...titleRows, ...spacer, header, ...body],
      sheet: s.sheet.slice(0, 31), // Excel's sheet-name limit
      columns: s.header.map((h, i) => ({ width: i === 0 ? 34 : Math.max(12, h.length + 4) })),
    };
  });
  // The library's cell types are stricter than this generic builder; the shapes above match its docs.
  await writeExcelFile(data as never).toFile(fileName);
}
